import React, { useState, useEffect } from 'react';
import { Plus, Eye, Edit, Search, X, Download } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function JefaturaEncuestas() {
    const [encuestas, setEncuestas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [selectedEncuesta, setSelectedEncuesta] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [errors, setErrors] = useState({});

    // Dropdowns data
    const [asignaturas, setAsignaturas] = useState([]);
    const [docentes, setDocentes] = useState([]);
    const [paralelos, setParalelos] = useState([]);
    const [asignacionesHorario, setAsignacionesHorario] = useState([]);

    // Preguntas dinámicas
    const [preguntas, setPreguntas] = useState([]);

    const [formData, setFormData] = useState({
        nombre: '',
        objetivo: '',
        id_asignatura: '',
        id_docente: '',
        id_paralelo: '',
        hora_publicacion: '',
        hora_cierre: ''
    });

    useEffect(() => {
        loadEncuestas();
        loadDropdowns();
    }, []);

    const loadEncuestas = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/jefatura/encuestas`);
            const data = await response.json();
            setEncuestas(data.encuestas || []);
        } catch (error) {
            console.error('Error:', error);
            setEncuestas([]);
        } finally {
            setLoading(false);
        }
    };

    const loadDropdowns = async () => {
        try {
            // Cargar asignaturas desde jefatura
            const asigRes = await fetch(`${API_URL}/jefatura/asignaturas`);
            if (asigRes.ok) {
                const asigData = await asigRes.json();
                const filteredAsignaturas = (asigData.asignaturas || []).filter(
                    a => !(a.nombre_completo || a.nombre || '').toLowerCase().includes('Archivo Obsoleto')
                );
                setAsignaturas(filteredAsignaturas);
            }
            // Cargar docentes desde jefatura
            const docRes = await fetch(`${API_URL}/jefatura/docentes`);
            if (docRes.ok) {
                const docData = await docRes.json();
                setDocentes(docData.docentes || []);
            }
            // Cargar asignaciones-horario para filtros dependientes
            const asigHorRes = await fetch(`${API_URL}/jefatura/asignaciones-horario?gestion=${new Date().getFullYear()}`);
            if (asigHorRes.ok) {
                const asigHorData = await asigHorRes.json();
                setAsignacionesHorario(asigHorData.asignaciones || []);
            }
            // Cargar paralelos/secciones desde jefatura
            const parRes = await fetch(`${API_URL}/jefatura/secciones`);
            if (parRes.ok) {
                const parData = await parRes.json();
                const paralelosData = (parData.secciones || []).map(s => ({
                    id_paralelo: s.id_seccion,
                    nombre: s.paralelo || s.modalidad || `Sección ${s.id_seccion}`
                }));
                setParalelos(paralelosData);
            }
        } catch (error) {
            console.error('Error loading dropdowns:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Reset dependientes al cambiar padre
        if (name === 'id_docente') {
            setFormData(prev => ({ ...prev, id_docente: value, id_asignatura: '', id_paralelo: '' }));
        } else if (name === 'id_asignatura') {
            setFormData(prev => ({ ...prev, id_asignatura: value, id_paralelo: '' }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    // Asignaturas filtradas por docente seleccionado
    const asignaturasFiltradas = (() => {
        if (!formData.id_docente) return asignaturas;
        const idsAsigDocente = [...new Set(
            asignacionesHorario
                .filter(ah => parseInt(ah.id_docente) === parseInt(formData.id_docente))
                .map(ah => parseInt(ah.id_asignatura))
        )];
        return asignaturas.filter(a => idsAsigDocente.includes(parseInt(a.id_asignatura)));
    })();

    // Paralelos filtrados por docente + asignatura seleccionados
    const paralelosFiltrados = (() => {
        if (!formData.id_asignatura) return [];
        return asignacionesHorario
            .filter(ah => {
                if (parseInt(ah.id_asignatura) !== parseInt(formData.id_asignatura)) return false;
                if (formData.id_docente && parseInt(ah.id_docente) !== parseInt(formData.id_docente)) return false;
                return true;
            })
            .map(ah => ah.paralelo)
            .filter((v, i, self) => v && self.indexOf(v) === i);
    })();

    // Manejo de preguntas
    const addPregunta = () => {
        setPreguntas([...preguntas, { texto: '', tipo: 'cerrado', opciones: ['', ''] }]);
    };

    const removePregunta = (index) => {
        setPreguntas(preguntas.filter((_, i) => i !== index));
    };

    const handlePreguntaChange = (index, field, value) => {
        const newPreguntas = [...preguntas];
        newPreguntas[index][field] = value;
        // Si cambia a abierto, limpiar opciones
        if (field === 'tipo' && value === 'abierto') {
            newPreguntas[index].opciones = [];
        } else if (field === 'tipo' && value === 'cerrado') {
            newPreguntas[index].opciones = ['', ''];
        }
        setPreguntas(newPreguntas);
    };

    const addOpcion = (preguntaIndex) => {
        const newPreguntas = [...preguntas];
        newPreguntas[preguntaIndex].opciones.push('');
        setPreguntas(newPreguntas);
    };

    const handleOpcionChange = (preguntaIndex, opcionIndex, value) => {
        const newPreguntas = [...preguntas];
        newPreguntas[preguntaIndex].opciones[opcionIndex] = value;
        setPreguntas(newPreguntas);
    };

    const removeOpcion = (preguntaIndex, opcionIndex) => {
        const newPreguntas = [...preguntas];
        if (newPreguntas[preguntaIndex].opciones.length > 2) {
            newPreguntas[preguntaIndex].opciones.splice(opcionIndex, 1);
            setPreguntas(newPreguntas);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.nombre.trim()) newErrors.nombre = 'Nombre es requerido';
        if (!formData.hora_cierre) newErrors.hora_cierre = 'Hora cierre es requerida';
        if (preguntas.length === 0) {
            newErrors.preguntas = 'Debe agregar al menos una pregunta';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const resetForm = () => {
        setFormData({
            nombre: '', objetivo: '', id_asignatura: '', id_docente: '',
            id_paralelo: '', hora_publicacion: '', hora_cierre: ''
        });
        setPreguntas([]);
        setEditingId(null);
        setErrors({});
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            const url = editingId
                ? `${API_URL}/jefatura/encuestas/${editingId}`
                : `${API_URL}/jefatura/encuestas`;

            const dataToSend = {
                ...formData,
                titulo: formData.nombre,
                descripcion: formData.objetivo,
                // Build local time string for PostgreSQL TIMESTAMP (no timezone)
                // datetime-local gives "YYYY-MM-DDTHH:MM" which is local time
                fecha_inicio: formData.hora_publicacion || (() => {
                    const n = new Date();
                    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}T${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}:${String(n.getSeconds()).padStart(2, '0')}`;
                })(),
                fecha_fin: formData.hora_cierre || null,
                preguntas: preguntas,
                estado: 'activa'
            };

            const response = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(dataToSend)
            });

            if (response.ok) {
                setShowModal(false);
                resetForm();
                loadEncuestas();
                alert('Encuesta publicada exitosamente!');
            } else {
                const error = await response.json();
                alert(error.error || 'Error al guardar encuesta');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al guardar encuesta');
        }
    };

    const handleEdit = (encuesta) => {
        setEditingId(encuesta.id_encuesta);

        let formularioData = {};
        if (typeof encuesta.formulario === 'string') {
            try { formularioData = JSON.parse(encuesta.formulario); } catch (e) { }
        } else if (typeof encuesta.formulario === 'object' && encuesta.formulario !== null) {
            formularioData = encuesta.formulario;
        }

        setFormData({
            nombre: encuesta.titulo || encuesta.nombre || '',
            objetivo: encuesta.descripcion || encuesta.objetivo || '',
            id_asignatura: encuesta.id_asignatura || formularioData.id_asignatura || '',
            id_docente: encuesta.id_docente || formularioData.id_docente || '',
            id_paralelo: encuesta.paralelo || encuesta.id_paralelo || formularioData.id_paralelo || formularioData.paralelo || '',
            // Backend returns raw local time strings via TO_CHAR (no TZ), safe to slice
            hora_publicacion: encuesta.fecha_inicio ? encuesta.fecha_inicio.slice(0, 16) : (encuesta.fecha_publicacion_str ? encuesta.fecha_publicacion_str.slice(0, 16) : ''),
            hora_cierre: encuesta.fecha_fin ? encuesta.fecha_fin.slice(0, 16) : (encuesta.fecha_cierre_str ? encuesta.fecha_cierre_str.slice(0, 16) : '')
        });

        let preguntasData = encuesta.preguntas || formularioData.preguntas || [];
        setPreguntas(preguntasData.length > 0 ? preguntasData : []);
        setShowModal(true);
    };

    const handleView = (encuesta) => {
        setSelectedEncuesta(encuesta);
        setShowViewModal(true);
    };

    const getEstadoColor = (estado, fechaFin) => {
        if (estado === 'cerrada' || (fechaFin && new Date(fechaFin) < new Date())) {
            return 'bg-gray-200 text-gray-800';
        }
        return 'bg-green-200 text-green-800';
    };

    const getEstado = (estado, fechaFin) => {
        if (estado === 'cerrada' || (fechaFin && new Date(fechaFin) < new Date())) {
            return 'Cerrada';
        }
        return 'Publicada';
    };

    const exportToExcel = () => {
        const headers = ['No', 'Nombre Encuesta', 'Objetivo', 'Asignatura', 'Docente', 'Paralelo', 'Hora Publicación', 'Hora Cierre', 'Estado'];
        const rows = filteredEncuestas.map((e, index) => [
            index + 1,
            e.titulo || e.nombre || '',
            e.descripcion || e.objetivo || '',
            e.asignatura || '',
            e.docente_nombre ? `${e.docente_nombre} ${e.docente_apellido || ''}` : '',
            e.paralelo || '',
            e.fecha_inicio ? new Date(e.fecha_inicio).toLocaleString() : '',
            e.fecha_fin ? new Date(e.fecha_fin).toLocaleString() : '',
            getEstado(e.estado, e.fecha_fin)
        ]);
        const csvContent = [headers.map(h => `"${h}"`).join(','), ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))].join('\n');
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `encuestas_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    };

    // Descargar respuestas de una encuesta específica
    const downloadRespuestas = async (encuesta) => {
        try {
            const response = await fetch(`${API_URL}/jefatura/encuestas/${encuesta.id_encuesta}/respuestas`);
            const data = await response.json();

            if (!response.ok) {
                alert(data.error || 'Error al obtener respuestas');
                return;
            }

            const { encuesta: enc, respuestas } = data;

            if (respuestas.length === 0) {
                alert('No hay respuestas registradas para esta encuesta');
                return;
            }

            // Construir headers basados en las preguntas del formulario
            // El formulario puede ser: { preguntas: [{texto: "...", tipo: "..."}] }
            let preguntasData = [];
            if (enc.formulario) {
                let formulario = enc.formulario;
                if (typeof formulario === 'string') {
                    try { formulario = JSON.parse(formulario); } catch (e) { formulario = {}; }
                }
                if (formulario.preguntas && Array.isArray(formulario.preguntas)) {
                    preguntasData = formulario.preguntas;
                }
            }
            const preguntasHeaders = preguntasData.map((p, i) => p.texto || p.pregunta || `Pregunta ${i + 1}`);

            const headers = ['No', 'Código Estudiante', 'Nombres', 'Apellidos', 'Fecha Respuesta', ...preguntasHeaders];

            const rows = respuestas.map((r, index) => {
                // Parsear respuestas del estudiante - formato: {"0": "respuesta1", "1": "respuesta2"}
                let respuestasObj = {};
                try {
                    if (typeof r.respuestas === 'string') {
                        respuestasObj = JSON.parse(r.respuestas);
                    } else if (typeof r.respuestas === 'object' && r.respuestas !== null) {
                        respuestasObj = r.respuestas;
                    }
                } catch (e) {
                    respuestasObj = {};
                }

                return [
                    index + 1,
                    r.codigo_estudiante || 'N/A',
                    r.nombres || '',
                    r.apellidos || '',
                    r.fecha_completado ? new Date(r.fecha_completado).toLocaleString() : '',
                    ...preguntasData.map((_, i) => respuestasObj[i] || respuestasObj[String(i)] || '')
                ];
            });

            // Crear hoja de cálculo con XLSX
            const wsData = [
                headers,
                ...rows
            ];

            const ws = XLSX.utils.aoa_to_sheet(wsData);

            // Ajustar anchos de columna
            const wscols = [
                { wch: 5 },  // No
                { wch: 15 }, // Código Estudiante
                { wch: 25 }, // Nombres
                { wch: 25 }, // Apellidos
                { wch: 20 }, // Fecha Respuesta
                ...preguntasHeaders.map(h => ({ wch: Math.max(h.length, 15) })) // Preguntas
            ];
            ws['!cols'] = wscols;

            // Añadir filtros automáticos
            const range = XLSX.utils.decode_range(ws['!ref']);
            ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } }) };

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Respuestas");

            // Generar y descargar archivo .xlsx
            XLSX.writeFile(wb, `Respuestas_${enc.nombre.replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`);

        } catch (error) {
            console.error('Error downloading respuestas:', error);
            alert('Error al descargar respuestas');
        }
    };

    const filteredEncuestas = encuestas.filter(e =>
        searchTerm === '' ||
        e.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.asignatura?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="p-6 text-center">Cargando...</div>;

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">Jefatura de Carrera &gt; Encuestas</h1>
            </div>

            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end">

                    <div className="relative">
                        <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">GESTIÓN</label>
                        <select
                            value={new Date().getFullYear()}
                            className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
                        >
                            {[2024, 2025, 2026, 2027].map(g => (
                                <option key={g} value={g}>Gestión {g}</option>
                            ))}
                        </select>
                        <span className="absolute right-3 top-[30px] pointer-events-none text-gray-400 text-xs">▼</span>
                    </div>



                    <div className="flex-grow flex items-end gap-2">
                        <div className="relative flex-grow">
                            <label className="block text-xs font-semibold text-gray-500 mb-1 ml-1">BUSCAR</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar..."
                                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                />
                            </div>
                        </div>
                        <button onClick={() => { resetForm(); setShowModal(true); }}
                            className="flex items-center gap-2 px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800">
                            <Plus size={20} /> Añadir Encuesta
                        </button>
                        <button onClick={exportToExcel} className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700" title="Descargar Excel">
                            <Download size={20} />
                        </button>

                    </div>
                </div>
            </div>

            {/* Buscador y botón añadir */}
            {/* <div className="mb-4 flex flex-wrap gap-3 items-center">
                <select
                    value={new Date().getFullYear()}
                    className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm"
                >
                    {[2024, 2025, 2026, 2027].map(g => (
                        <option key={g} value={g}>Gestión {g}</option>
                    ))}
                </select>
                <button onClick={() => { resetForm(); setShowModal(true); }}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-full hover:bg-blue-700">
                    <Plus size={20} /> Añadir Encuesta
                </button>
                <div className="flex-1 max-w-md relative">
                    <input type="text" placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full" />
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                </div>
                <button onClick={exportToExcel} className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700" title="Descargar Excel">
                    <Download size={20} />
                </button>
            </div> */}

            {/* Tabla de encuestas */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-3 text-center font-semibold border">No</th>
                                <th className="px-3 py-3 text-center font-semibold border">Nombre Encuesta</th>
                                <th className="px-3 py-3 text-center font-semibold border">Objetivo</th>
                                <th className="px-3 py-3 text-center font-semibold border">Asignatura</th>
                                <th className="px-3 py-3 text-center font-semibold border">Docente</th>
                                <th className="px-3 py-3 text-center font-semibold border">Paralelo</th>
                                <th className="px-3 py-3 text-center font-semibold border">Hora Publicación</th>
                                <th className="px-3 py-3 text-center font-semibold border">Hora Presentación</th>
                                <th className="px-3 py-3 text-center font-semibold border">Estado</th>
                                <th className="px-3 py-3 text-center font-semibold border">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {filteredEncuestas.length === 0 ? (
                                <tr><td colSpan="10" className="px-4 py-8 text-center text-gray-500">No hay encuestas registradas</td></tr>
                            ) : (
                                filteredEncuestas.map((e, index) => (
                                    <tr key={e.id_encuesta} className="hover:bg-gray-50">
                                        <td className="px-3 py-2 text-center border">{index + 1}</td>
                                        <td className="px-3 py-2 border font-medium">{e.titulo || e.nombre || '-'}</td>
                                        <td className="px-3 py-2 border">{e.descripcion || e.objetivo || '-'}</td>
                                        <td className="px-3 py-3">{e.asignatura || '-'}</td>
                                        <td className="px-3 py-2 border">{e.docente_nombre ? `${e.docente_nombre} ${e.docente_apellido || ''}` : '-'}</td>
                                        <td className="px-3 py-2 border">{e.paralelo || '-'}</td>
                                        <td className="px-3 py-2 border">{e.fecha_inicio ? new Date(e.fecha_inicio).toLocaleString() : '-'}</td>
                                        <td className="px-3 py-2 border">{e.fecha_fin ? new Date(e.fecha_fin).toLocaleString() : '-'}</td>
                                        <td className="px-3 py-2 border text-center">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(e.estado, e.fecha_fin)}`}>
                                                {getEstado(e.estado, e.fecha_fin)}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3">
                                            <div className="flex justify-center gap-2">
                                                <button onClick={() => handleView(e)} className="p-2 rounded-full transition shadow-sm bg-blue-100 text-blue-600 hover:bg-blue-200" title="Ver resultados">
                                                    <Eye size={16} />
                                                </button>
                                                <button onClick={() => handleEdit(e)} className="p-2 rounded-full transition shadow-sm bg-yellow-100 text-yellow-600 hover:bg-yellow-200" title="Editar">
                                                    <Edit size={16} />
                                                </button>
                                                <button onClick={() => downloadRespuestas(e)} className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700" title="Descargar respuestas">
                                                    <Download size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal Añadir/Editar Encuesta */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-blue-900">Añadir Encuesta</h2>
                            <button onClick={() => { setShowModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            {/* Nombre y Objetivo */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Nombre Encuesta <span className="text-red-500">*</span></label>
                                    <input type="text" name="nombre" value={formData.nombre} onChange={handleInputChange}
                                        placeholder="Ingrese nombre de la encuesta"
                                        className={`w-full px-3 py-2 border-2 rounded-md ${errors.nombre ? 'border-red-500' : 'border-gray-300'}`} />
                                    {errors.nombre && <p className="mt-1 text-sm text-red-600">{errors.nombre}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Objetivo</label>
                                    <input type="text" name="objetivo" value={formData.objetivo} onChange={handleInputChange}
                                        placeholder="Describa el objetivo de la encuesta"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md" />
                                </div>
                            </div>

                            {/* Docente → Asignatura → Paralelo (filtros dependientes) */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Docente</label>
                                    <select name="id_docente" value={formData.id_docente} onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md">
                                        <option value="">Seleccionar</option>
                                        {docentes.map(d => (
                                            <option key={d.id_docente} value={d.id_docente}>{d.nombres || d.nombre} {d.apellidos || d.apellido}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Asignatura</label>
                                    <select name="id_asignatura" value={formData.id_asignatura} onChange={handleInputChange}
                                        disabled={!formData.id_docente}
                                        className={`w-full px-3 py-2 border border-gray-300 rounded-md ${!formData.id_docente ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}>
                                        <option value="">{formData.id_docente ? 'Seleccionar' : 'Seleccione un docente primero'}</option>
                                        {asignaturasFiltradas.map(a => (
                                            <option key={a.id_asignatura} value={a.id_asignatura}>{a.nombre_completo || a.nombre}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Paralelo</label>
                                    <select name="id_paralelo" value={formData.id_paralelo} onChange={handleInputChange}
                                        disabled={!formData.id_asignatura}
                                        className={`w-full px-3 py-2 border border-gray-300 rounded-md ${!formData.id_asignatura ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}>
                                        <option value="">{formData.id_asignatura ? 'Seleccionar' : 'Seleccione una asignatura primero'}</option>
                                        {paralelosFiltrados.map(p => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Hora Cierre - Solo este campo */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Fecha Cierre <span className="text-red-500">*</span></label>
                                    <input type="datetime-local" name="hora_cierre" value={formData.hora_cierre} onChange={handleInputChange}
                                        className={`w-full px-3 py-2 border-2 rounded-md ${errors.hora_cierre ? 'border-red-500' : 'border-gray-300'}`} />
                                    {errors.hora_cierre && <p className="mt-1 text-sm text-red-600">{errors.hora_cierre}</p>}
                                    <p className="text-xs text-gray-500 mt-1">La fecha de publicación se registrará automáticamente al crear la encuesta</p>
                                </div>
                            </div>

                            {/* Separador y botón añadir pregunta */}
                            <div className="border-t pt-4">
                                <div className="flex items-center gap-2 mb-4">
                                    <button onClick={addPregunta} className="w-8 h-8 flex items-center justify-center bg-blue-900 text-white rounded-full hover:bg-blue-800">
                                        <Plus size={16} />
                                    </button>
                                    <span className="text-sm text-gray-600">Agregar pregunta</span>
                                </div>
                                {errors.preguntas && <p className="mb-2 text-sm text-red-600">{errors.preguntas}</p>}

                                {/* Lista de preguntas */}
                                <div className="space-y-4">
                                    {preguntas.map((pregunta, pIndex) => (
                                        <div key={pIndex} className="border rounded-lg p-4 bg-gray-50">
                                            <div className="flex items-center gap-2 mb-3">
                                                <span className="font-medium">{pIndex + 1}.</span>
                                                <input
                                                    type="text"
                                                    value={pregunta.texto}
                                                    onChange={(e) => handlePreguntaChange(pIndex, 'texto', e.target.value)}
                                                    placeholder="Escriba aquí su pregunta..."
                                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                                                />
                                                <select
                                                    value={pregunta.tipo}
                                                    onChange={(e) => handlePreguntaChange(pIndex, 'tipo', e.target.value)}
                                                    className="px-3 py-2 border border-gray-300 rounded-md"
                                                >
                                                    <option value="cerrado">Cerrado</option>
                                                    <option value="abierto">Abierto</option>
                                                </select>
                                                <button onClick={() => removePregunta(pIndex)} className="p-1 text-red-500 hover:text-red-700">
                                                    <X size={20} />
                                                </button>
                                            </div>

                                            {/* Opciones para pregunta cerrada */}
                                            {pregunta.tipo === 'cerrado' && (
                                                <div className="ml-6 space-y-2">
                                                    {pregunta.opciones.map((opcion, oIndex) => (
                                                        <div key={oIndex} className="flex items-center gap-2">
                                                            <input type="radio" disabled className="w-4 h-4" />
                                                            <input
                                                                type="text"
                                                                value={opcion}
                                                                onChange={(e) => handleOpcionChange(pIndex, oIndex, e.target.value)}
                                                                placeholder={`Opción ${oIndex + 1}`}
                                                                className="flex-1 px-3 py-1 border border-gray-300 rounded-md text-sm"
                                                            />
                                                            {pregunta.opciones.length > 2 && (
                                                                <button onClick={() => removeOpcion(pIndex, oIndex)} className="text-red-400 hover:text-red-600">
                                                                    <X size={16} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}
                                                    <button onClick={() => addOpcion(pIndex)} className="text-blue-600 hover:text-blue-800 text-sm">
                                                        + Agregar opción
                                                    </button>
                                                </div>
                                            )}

                                            {/* Área de texto para pregunta abierta */}
                                            {pregunta.tipo === 'abierto' && (
                                                <div className="ml-6">
                                                    <textarea
                                                        disabled
                                                        placeholder="El estudiante escribirá su respuesta aquí..."
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-400"
                                                        rows={2}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Botón Publicar */}
                            <div className="flex justify-end pt-4">
                                <button onClick={handleSubmit} className="px-8 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800">
                                    Publicar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Ver Resultados */}
            {showViewModal && selectedEncuesta && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-blue-900">Resultados de Encuesta</h2>
                            <button onClick={() => { setShowViewModal(false); setSelectedEncuesta(null); }} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Nombre</label>
                                    <p className="text-lg font-semibold">{selectedEncuesta.titulo || selectedEncuesta.nombre}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Objetivo</label>
                                    <p>{selectedEncuesta.descripcion || selectedEncuesta.objetivo || '-'}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Asignatura</label>
                                    <p>{selectedEncuesta.asignatura || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Docente</label>
                                    <p>{selectedEncuesta.docente_nombre || '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Paralelo</label>
                                    <p>{selectedEncuesta.paralelo || '-'}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Hora Publicación</label>
                                    <p>{selectedEncuesta.fecha_inicio ? new Date(selectedEncuesta.fecha_inicio).toLocaleString() : '-'}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Hora Cierre</label>
                                    <p>{selectedEncuesta.fecha_fin ? new Date(selectedEncuesta.fecha_fin).toLocaleString() : '-'}</p>
                                </div>
                            </div>
                            <div className="border-t pt-4">
                                <label className="block text-sm font-medium text-gray-500 mb-2">Preguntas y Respuestas</label>
                                <div className="space-y-3">
                                    {(selectedEncuesta.preguntas?.preguntas || selectedEncuesta.formulario?.preguntas || []).map((p, i) => (
                                        <div key={i} className="p-3 bg-gray-50 rounded-md">
                                            <p className="font-medium">{i + 1}. {p.texto}</p>
                                            <p className="text-sm text-gray-500">Tipo: {p.tipo === 'cerrado' ? 'Cerrado (opciones)' : 'Abierto (texto libre)'}</p>
                                            {p.tipo === 'cerrado' && p.opciones && (
                                                <div className="mt-2 ml-4">
                                                    {p.opciones.map((op, j) => (
                                                        <div key={j} className="flex items-center gap-2 text-sm">
                                                            <span className="w-4 h-4 border border-gray-400 rounded-full"></span>
                                                            <span>{op || `Opción ${j + 1}`}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="flex justify-end pt-4">
                                <button onClick={() => { setShowViewModal(false); setSelectedEncuesta(null); }} className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800">Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
